import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetIFRSById, IfrsQuestions } from "../../../api/Quiz/GetIFRSById";
import { useUserDataStore } from "../../../stores/user";

export const useExam = () => {
  const { exam_id, question_id } = useParams();
  const navigate = useNavigate();
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  // --- Exam Data ---
  const {
    data: examData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["ifrs-exam", "ifrs-exam-content", exam_id],
    queryFn: async () => {
      if (!exam_id) return null;
      const response = await GetIFRSById(exam_id);
      return response.data;
    },
    enabled: !!exam_id,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const { data: questionData, isLoading: isQuestionLoading } = useQuery({
    queryKey: ["ifrs-exam", "ifrs-question-details", exam_id, question_id],
    queryFn: async () => {
      if (!exam_id || !question_id) return null;
      const { GetIFRSQuestionById } = await import(
        "../../../api/Quiz/GetIFRSQuestionById"
      );
      const response = await GetIFRSQuestionById(exam_id, question_id);
      return response.data;
    },
    enabled: !!exam_id && !!question_id && userRole !== "admin",
    refetchOnWindowFocus: false,
  });

  // --- Questions List Data (Navigator) ---
  const { data: questionsList } = useQuery({
    queryKey: ["ifrs-exam", "ifrs-questions-list", exam_id],
    queryFn: async () => {
      if (!exam_id) return [];
      const { GetIFRSQuestions } = await import(
        "../../../api/Quiz/GetIFRSQuestions"
      );
      const response = await GetIFRSQuestions(exam_id, { limit: 100 });
      return response.data || [];
    },
    enabled: !!exam_id,
    refetchOnWindowFocus: false,
  });

  const questions = useMemo(
    () => (questionsList || []) as any as IfrsQuestions[],
    [questionsList]
  );

  const totalQuestions = useMemo(() => questions.length, [questions]);

  // --- Exhibits Data ---
  const { data: exhibitsData } = useQuery({
    queryKey: ["ifrs-exam", "ifrs-question-exhibits", question_id],
    queryFn: async () => {
      if (!question_id) return;
      const { GetIFRSQuestionExhibits } = await import(
        "../../../api/Quiz/GetIFRSQuestionExhibits"
      );
      const response = await GetIFRSQuestionExhibits(question_id);
      return response.data;
    },
    enabled: !!question_id,
    refetchOnWindowFocus: false,
  });

  // --- Response Options Data ---
  const { data: responseOptionsData } = useQuery({
    queryKey: ["ifrs-exam", "ifrs-question-response-options", question_id],
    queryFn: async () => {
      if (!question_id) return;
      const { GetIFRSQuestionResponseOptions } = await import(
        "../../../api/Quiz/GetIFRSQuestionResponseOptions"
      );
      const response = await GetIFRSQuestionResponseOptions(question_id);
      return response.data;
    },
    enabled: !!question_id,
    refetchOnWindowFocus: false,
  });

  // --- Navigation Helpers ---
  const goToQuestion = (index: number) => {
    if (index >= 0 && index < totalQuestions) {
      const targetId = questions[index].id;
      navigate(`/ifrs-exam/${exam_id}/exam/${targetId}`);
    }
  };

  const nextQuestion = () => goToQuestion(currentQuestionIndex + 1);
  const prevQuestion = () => goToQuestion(currentQuestionIndex - 1);

  // --- Flags Management ---
  const getFlaggedQuestionsVal = () => {
    try {
      const stored = sessionStorage.getItem(`flagged-questions-${exam_id}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const [flaggedQuestions, setFlaggedQuestions] = useState<string[]>([]);

  useEffect(() => {
    setFlaggedQuestions(getFlaggedQuestionsVal());
  }, [exam_id]);

  const toggleFlag = useCallback(
    (qId: string) => {
      setFlaggedQuestions((prev) => {
        const isFlagged = prev.includes(qId);
        const newFlags = isFlagged
          ? prev.filter((id) => id !== qId)
          : [...prev, qId];
        sessionStorage.setItem(
          `flagged-questions-${exam_id}`,
          JSON.stringify(newFlags)
        );
        return newFlags;
      });
    },
    [exam_id]
  );

  const isFlagged = useCallback(
    (qId: string) => flaggedQuestions.includes(qId),
    [flaggedQuestions]
  );

  // --- Seen Questions Management ---
  const [seenQuestions, setSeenQuestions] = useState<string[]>([]);

  useEffect(() => {
    // Initialize or load seen questions
    const stored = sessionStorage.getItem(`seen-questions-${exam_id}`);
    if (stored) {
      setSeenQuestions(JSON.parse(stored));
    }
  }, [exam_id]);

  useEffect(() => {
    if (question_id && !seenQuestions.includes(question_id)) {
      const newSeen = [...seenQuestions, question_id];
      setSeenQuestions(newSeen);
      sessionStorage.setItem(
        `seen-questions-${exam_id}`,
        JSON.stringify(newSeen)
      );
    }
  }, [question_id, seenQuestions, exam_id]);

  // --- Derived State (Memoized) ---

  const currentQuestionIndex = useMemo(() => {
    return questions.findIndex((q: any) => q.id === question_id);
  }, [questions, question_id]);

  const currentQuestion = useMemo(() => {
    return questions[currentQuestionIndex];
  }, [questions, currentQuestionIndex]);

  // --- Student Exam ID (Memoized) ---
  const studentExamId = useMemo(() => {
    if (!exam_id) return "";
    return localStorage.getItem(`ifrs-student-exam-id-${exam_id}`) || "";
  }, [exam_id]);

  // ... (Queries remain the same) ...

  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion =
    totalQuestions > 0 && currentQuestionIndex === totalQuestions - 1;

  // --- Return Object (Memoized) ---
  return useMemo(
    () => ({
      // Data
      examData,
      questions,
      totalQuestions,
      currentQuestionIndex,
      currentQuestionId: question_id,
      currentQuestion,
      isLoading,
      isError,

      // Identifiers
      examId: exam_id,
      studentExamId,

      // Navigation
      goToQuestion,
      nextQuestion,
      prevQuestion,
      isFirstQuestion,
      isLastQuestion,

      // Flags
      flaggedQuestions,
      toggleFlag,
      isFlagged,

      // Seen
      seenQuestions,
      isSeen: (id: string) => seenQuestions.includes(id),

      // Question Detail
      questionData,
      isQuestionLoading,
      exhibitsData,
      responseOptionsData,
    }),
    [
      examData,
      questions,
      totalQuestions,
      currentQuestionIndex,
      question_id,
      currentQuestion,
      isLoading,
      isError,
      exam_id,
      studentExamId,
      goToQuestion,
      nextQuestion,
      prevQuestion,
      isFirstQuestion,
      isLastQuestion,
      flaggedQuestions,
      toggleFlag,
      isFlagged,
      seenQuestions,
      questionData,
      isQuestionLoading,
      exhibitsData,
      responseOptionsData,
    ]
  );
};

export default useExam;
